import React, { useState, useContext } from "react";
import { useSelector } from "react-redux";
import { Typography, CircularProgress } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "../../../../../hooks/useTranslation";
import BundleKey from "../../../../../translation/bundleKey";
import FormTextInputWithButton from "../../TextInput/FormTextInputWithButton";
import AccessTypeResultCard from "../../Cards/AccessTypeResultCard/AccessTypeResultCard";
import {
  buildAuthorizationSchedule,
  computeScheduleStatus,
} from "../../Cards/AccessTypeResultCard/utils";
import AccordionCard from "../../AccordionCard/AccordionCard";
import { RootState } from "../../../../../redux/reducers/rootReducer";
import { useEmployeeSearch } from "../../../../../hooks/useEmployeeSearch";
import { formatManualAuthorizer } from "../../Table/utils/getTransportAuthorizer";
import { COMMUNICATION_METHOD_LABELS } from "../../../../../views/LogisticPark/RegistroIngreso/components/AccessTypeForm/TransportContentForm/RegistrarAutorizacionModal";
import { RegistroIngresoSessionContext } from "../../../../../views/LogisticPark/RegistroIngreso/session/RegistroIngresoSessionContext";
import "./AccessTypeFormContent.css";

export interface AuthorizationsSectionProps {
  id: string;
  disabled?: boolean;
  hasError?: boolean;
  isCompanion?: boolean;

  searchFieldName: string;
  confirmationsFieldName: string;
  confirmationsDataFieldName: string;
  prevalidationsFieldName: string;
  invitesFieldName: string;
  authorizationsFieldName: string;
  companyFieldName: string;

  authorizationsList: any[];
  isLoadingAuths?: boolean;
  inheritedAuthorizations?: any[];
  className?: string;
}

const AuthorizationsSection: React.FC<AuthorizationsSectionProps> = ({
  id,
  disabled = false,
  hasError = false,
  isCompanion = false,
  searchFieldName,
  confirmationsFieldName,
  confirmationsDataFieldName,
  prevalidationsFieldName,
  invitesFieldName,
  authorizationsFieldName,
  companyFieldName,
  authorizationsList = [],
  isLoadingAuths = false,
  inheritedAuthorizations,
  className,
}) => {
  const { watch, setValue } = useFormContext();
  const { t } = useTranslation();
  const allCompanies = useSelector((state: RootState) => state.auth.companies);
  const session = useContext(RegistroIngresoSessionContext);

  const activeTab = session?.tabs?.find((tab) => tab.id === session.activeTabId);
  const isExitForm = activeTab?.mode === "exit";

  const searchValue = (watch(searchFieldName) || "").toString();
  const normalizedSearch = searchValue.trim().toLowerCase();

  const { employees, isLoading: isEmployeeSearchLoading } = useEmployeeSearch(normalizedSearch);

  const currentConfirmations = watch(confirmationsFieldName) || [];
  const currentPrevalidations = watch(prevalidationsFieldName) || [];
  const currentInvites = watch(invitesFieldName) || [];
  const currentAuthorizations = watch(authorizationsFieldName) || [];
  const selectedEmployeesRaw = watch(confirmationsDataFieldName) || [];

  // 1. Convert raw authorizations list (declared watch fields above to resolve TS2448)
  const authorizationsResults = React.useMemo(() => {
    if (!authorizationsList || authorizationsList.length === 0) return [];
    return authorizationsList.map((auth: any) => {
      const companyObj = allCompanies?.find((c: any) => c.id === auth.company);
      const company = companyObj?.name || companyObj?.description || auth.guestCompany || "N/A";

      const host = auth.host || auth.manager;
      const managerName = host ? `${host.firstname} ${host.lastname}` : "N/A";
      const managerPhone = host?.phone || "N/A";

      const statusLabel =
        auth._source === "invite"
          ? "Invitación"
          : auth._source === "authorization"
          ? "Autorización"
          : auth.type || "Autorización";

      // Las invitaciones y autorizaciones de `getAuthorizations` traen la vigencia en `calendar` +
      // `beginDatetime`/`endDatetime`, y su `status` ("accepted"/"pending") no dice si está vigente:
      // hay que calcularlo. Las prevalidaciones ya vienen planas y con `status` "valid"/"invalid"
      // resuelto por el backend, así que se respeta tal cual.
      const schedule = buildAuthorizationSchedule(auth);
      const isPassAuthorization = auth._source === "invite" || auth._source === "authorization";

      return {
        id: auth.id,
        hostId: host?.id || "",
        companyId: auth.company || "",
        company,
        managerName,
        managerPhone,
        status: statusLabel,
        type: "authorization",
        _source: auth._source,
        ...schedule,
        authStatus: isPassAuthorization
          ? computeScheduleStatus(auth, schedule)
          : auth.status || null,
        accessByGuard: Boolean(auth.accessByGuard),
        suspended: Boolean(auth.suspended),
        hostObj: host || null,
      };
    });
  }, [authorizationsList, allCompanies]);

  const filteredAuthorizationsResults = React.useMemo(() => {
    let list = authorizationsResults;

    // 1. Filter out inherited authorizations
    if (inheritedAuthorizations && inheritedAuthorizations.length > 0) {
      list = list.filter((result: any) => {
        return !inheritedAuthorizations.some((inherited: any) => {
          // Same-type match (authorization vs authorization)
          if (result.type === inherited.type) {
            return result.id === inherited.id;
          }
          // Cross-type match (authorization vs employee): compare companyId + host
          if (result.companyId && inherited.companyId && result.companyId === inherited.companyId) {
            // Match by host ID if available
            if (result.hostId && inherited.realId) {
              return result.hostId === inherited.realId;
            }
            // Fallback: match by manager name + phone
            return (
              result.managerName === inherited.managerName &&
              result.managerPhone === inherited.managerPhone
            );
          }
          return false;
        });
      });
    }

    // 2. Filter out authorizations that match selectedEmployees (guard-selected confirmations)
    if (selectedEmployeesRaw && selectedEmployeesRaw.length > 0) {
      list = list.filter((result: any) => {
        return !selectedEmployeesRaw.some((empRaw: any) => {
          // Out-of-range prevalidations converted to guard confirmations must only
          // hide their own card, not every authorization sharing the same host/company.
          if (empRaw.type === "guard" && empRaw.authStatus) {
            return result.id === empRaw.id;
          }

          // Normalize empRaw to get realId and companyId
          let realId = empRaw.realId;
          let companyId = empRaw.companyId || empRaw.company;
          if (empRaw.type === "guard" && empRaw.host) {
            realId = empRaw.host.id || empRaw.host;
          }

          if (result.companyId && companyId && result.companyId === companyId) {
            if (result.hostId && realId) {
              return result.hostId === realId;
            }
            // Fallback: match manager name / phone
            const host = empRaw.type === "guard" ? empRaw.host : null;
            const empName = host ? `${host.firstname} ${host.lastname}`.trim() : empRaw.employee;
            const empPhone = host?.phone || empRaw.phone;
            if (empName && empName !== "N/A") {
              return (
                result.managerName === empName &&
                (empPhone === "N/A" || result.managerPhone === empPhone)
              );
            }
          }
          return false;
        });
      });
    }

    return list;
  }, [authorizationsResults, inheritedAuthorizations, selectedEmployeesRaw]);

  const isChecked = (result: any) => {
    if (result.type === "employee") {
      return currentConfirmations.some(
        (c: any) => c.host === result.realId && c.company === result.companyId
      );
    }
    if (result._source === "invite") return currentInvites.includes(result.id);
    if (result._source === "authorization") return currentAuthorizations.includes(result.id);
    if (result.authStatus === "invalid") {
      return selectedEmployeesRaw.some((e: any) => e.id === result.id);
    }
    return currentPrevalidations.includes(result.id);
  };

  // 2. Format selected guard employees
  const selectedEmployees = React.useMemo(() => {
    const formatted = selectedEmployeesRaw.map((emp: any) => {
      if (emp.type === "employee" && emp.employee) return emp;

      if (emp.type === "guard") {
        const companyObj = allCompanies?.find((c: any) => c.id === emp.company);
        const companyName = companyObj?.name || companyObj?.description || "N/A";
        const host = emp.host;
        // El acompañante del transportista entra sin anfitrión: lo respalda el responsable que el
        // guardia cargó a mano al autorizar el acceso (`authorizedBy` de la invitación).
        const manualAuthorizer = !host ? (emp.authorizedBy || "").toString().trim() : "";
        const name = host
          ? `${host.firstname} ${host.lastname}`.trim()
          : manualAuthorizer
          ? formatManualAuthorizer(manualAuthorizer)
          : "N/A";
        // Sin anfitrión no hay teléfono que mostrar: ese slot pasa a decir por qué medio el
        // guardia consiguió la autorización que cargó a mano.
        const manualMethod = !host
          ? COMMUNICATION_METHOD_LABELS[
              emp.communicationMethod as keyof typeof COMMUNICATION_METHOD_LABELS
            ] || ""
          : "";

        return {
          id: emp.id || `${host?.id || "unknown"}-${emp.company}`,
          realId: host?.id || host,
          companyId: emp.company,
          company: companyName,
          employee: name,
          phone: host?.phone || manualMethod || "N/A",
          // `undefined` deja el default "Teléfono" del card para el resto de las confirmaciones.
          phoneLabel: manualMethod ? "Medio" : undefined,
          status: "Empleado",
          type: "employee",
          from: emp.from ?? null,
          to: emp.to ?? null,
          fromHour: emp.fromHour ?? null,
          toHour: emp.toHour ?? null,
          days: emp.days ?? null,
          authStatus: emp.authStatus ?? null,
        };
      }

      return emp;
    });

    return formatted.filter((emp: any) => {
      // Out-of-range prevalidations converted to guard confirmations are unique per
      // prevalidation id — never dedupe them by host/company against unrelated
      // authorizations that merely share the same host/company.
      if (emp.authStatus) {
        const isDuplicatedById =
          inheritedAuthorizations?.some((inherited: any) => inherited.id === emp.id) ?? false;
        return !isDuplicatedById;
      }

      const isDuplicatedInInherited =
        inheritedAuthorizations?.some((inherited: any) => {
          if (emp.type === inherited.type) {
            if (emp.type === "employee") {
              return emp.realId === inherited.realId && emp.companyId === inherited.companyId;
            }
            return emp.id === inherited.id;
          }
          // Cross-type match (employee vs authorization)
          const empCompanyId = emp.companyId || emp.company;
          const inheritedCompanyId = inherited.companyId || inherited.company;
          if (empCompanyId && inheritedCompanyId && empCompanyId === inheritedCompanyId) {
            const empHostId = emp.realId;
            const inheritedHostId = inherited.realId || inherited.hostId;
            if (empHostId && inheritedHostId) {
              return empHostId === inheritedHostId;
            }
          }
          return false;
        }) ?? false;

      const isDuplicatedInActive =
        filteredAuthorizationsResults?.some((active: any) => {
          if (emp.type === active.type) {
            if (emp.type === "employee") {
              return emp.realId === active.realId && emp.companyId === active.companyId;
            }
            return emp.id === active.id;
          }
          // Cross-type match (employee vs authorization)
          const empCompanyId = emp.companyId || emp.company;
          const activeCompanyId = active.companyId || active.company;
          if (empCompanyId && activeCompanyId && empCompanyId === activeCompanyId) {
            const empHostId = emp.realId;
            const activeHostId = active.hostId || active.realId;
            if (empHostId && activeHostId) {
              return empHostId === activeHostId;
            }
          }
          return false;
        }) ?? false;

      return !isDuplicatedInInherited && !isDuplicatedInActive;
    });
  }, [selectedEmployeesRaw, allCompanies, inheritedAuthorizations, filteredAuthorizationsResults]);

  // 3. Employee results & manual search filtering
  const employeeResults = React.useMemo(() => {
    const results: any[] = [];
    employees?.forEach((emp: any) => {
      const name = `${emp.firstname} ${emp.lastname}`;
      const workplaces = emp.workplaces || [];

      if (workplaces.length > 0) {
        workplaces.forEach((wp: any, idx: number) => {
          const companyId = wp.company;
          const companyObj = allCompanies?.find((c: any) => c.id === companyId);
          const companyName = companyObj?.name || companyObj?.description || "N/A";

          results.push({
            id: `${emp.id}-${companyId}-${idx}`,
            realId: emp.id,
            companyId: companyId,
            company: companyName,
            employee: name,
            phone: emp.phone || "N/A",
            status: "Empleado",
            type: "employee",
          });
        });
      } else {
        results.push({
          id: emp.id,
          realId: emp.id,
          company: "N/A",
          employee: name,
          phone: emp.phone || "N/A",
          status: "Empleado",
          type: "employee",
        });
      }
    });
    return results;
  }, [employees, allCompanies]);

  const searchResults = normalizedSearch
    ? employeeResults.filter((item: any) => {
        const searchable = `${item.employee || ""} ${item.company} ${item.managerName || ""} ${
          item.phone || ""
        }`.toLowerCase();
        return searchable.includes(normalizedSearch) && !isChecked(item);
      })
    : [];

  // 4. Checkbox toggled
  const handleCheckedChange = (result: any, checked: boolean) => {
    if (disabled) return;
    if (result.type === "employee") {
      const currentData = watch(confirmationsDataFieldName) || [];
      const nextValue = checked
        ? [...currentConfirmations, { host: result.realId, company: result.companyId }]
        : currentConfirmations.filter(
            (c: any) => !(c.host === result.realId && c.company === result.companyId)
          );

      const nextData = checked
        ? [...currentData, result]
        : currentData.filter((emp: any) => emp.id !== result.id);

      setValue(confirmationsFieldName, nextValue);
      setValue(confirmationsDataFieldName, nextData);

      if (checked) {
        setValue(searchFieldName, "");
      }
    } else {
      const isInvite = result._source === "invite";
      const isAuth = result._source === "authorization";
      const isOutOfRangePrevalidation = !isInvite && !isAuth && result.authStatus === "invalid";

      if (isOutOfRangePrevalidation) {
        const currentData = watch(confirmationsDataFieldName) || [];
        const nextConfirmations = checked
          ? result.hostId
            ? [...currentConfirmations, { host: result.hostId, company: result.companyId }]
            : currentConfirmations
          : currentConfirmations.filter(
              (c: any) => !(c.host === result.hostId && c.company === result.companyId)
            );
        const nextData = checked
          ? [
              ...currentData,
              {
                type: "guard",
                id: result.id,
                host: result.hostObj,
                company: result.companyId,
                from: result.from,
                to: result.to,
                fromHour: result.fromHour,
                toHour: result.toHour,
                days: result.days,
                authStatus: result.authStatus,
              },
            ]
          : currentData.filter((emp: any) => emp.id !== result.id);

        setValue(confirmationsFieldName, nextConfirmations);
        setValue(confirmationsDataFieldName, nextData);

        if (checked && result.companyId) {
          setValue(companyFieldName, result.companyId);
        }
        return;
      }

      const list = isInvite
        ? currentInvites
        : isAuth
        ? currentAuthorizations
        : currentPrevalidations;
      const nextValue = checked
        ? [...list, result.id]
        : list.filter((id: string) => id !== result.id);

      if (isInvite) setValue(invitesFieldName, nextValue);
      else if (isAuth) setValue(authorizationsFieldName, nextValue);
      else setValue(prevalidationsFieldName, nextValue);

      if (checked && result.companyId) {
        setValue(companyFieldName, result.companyId);
      } else if (!checked) {
        const newInvites = isInvite ? nextValue : currentInvites;
        const newAuths = isAuth ? nextValue : currentAuthorizations;
        const newPrevals = !isInvite && !isAuth ? nextValue : currentPrevalidations;

        const allAuths = [...filteredAuthorizationsResults, ...(inheritedAuthorizations || [])];
        const remaining = allAuths.find(
          (r: any) =>
            newInvites.includes(r.id) || newAuths.includes(r.id) || newPrevals.includes(r.id)
        );
        setValue(companyFieldName, remaining?.companyId || "");
      }
    }
  };

  const content = (
    <div className="accessTypeAuthorizationsContent">
      {/* Destinos heredados del conductor (optional for companions) */}
      {inheritedAuthorizations && inheritedAuthorizations.length > 0 && (
        <div className="accessTypeSection">
          <p className="accessTypeSectionTitle">Destinos heredados del conductor</p>
          <div className="accessTypeResultsList">
            {inheritedAuthorizations.map((result: any) => (
              <AccessTypeResultCard
                key={`driver-${result.id}`}
                label={result.company}
                status={result.status}
                checked={isChecked(result)}
                managerName={result.managerName}
                managerPhone={result.managerPhone}
                checkboxPosition="left"
                onCheckedChange={(checked) => handleCheckedChange(result, checked)}
                disabled={disabled}
                showAuthorizationDetails={!isCompanion}
                accessByGuard={result.accessByGuard}
                suspended={result.suspended}
                authorizationSchedule={{
                  from: result.from,
                  to: result.to,
                  fromHour: result.fromHour,
                  toHour: result.toHour,
                  days: result.days,
                  status: result.authStatus,
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Active Authorizations / Empty State */}
      <div className="accessTypeSection">
        {inheritedAuthorizations && (
          <p className="accessTypeSectionTitle">Autorizaciones vigentes del acompañante</p>
        )}
        <div className="accessTypeResultsList">
          {isLoadingAuths ? (
            <div className="accessTypeSearchLoading">
              <CircularProgress size={30} />
            </div>
          ) : filteredAuthorizationsResults.length > 0 ? (
            filteredAuthorizationsResults.map((result: any) => (
              <AccessTypeResultCard
                key={result.id}
                label={result.company}
                status={result.status}
                checked={isChecked(result)}
                managerName={result.managerName}
                managerPhone={result.managerPhone}
                checkboxPosition="left"
                onCheckedChange={(checked) => handleCheckedChange(result, checked)}
                disabled={disabled}
                showAuthorizationDetails={!isCompanion}
                accessByGuard={result.accessByGuard}
                suspended={result.suspended}
                authorizationSchedule={{
                  from: result.from,
                  to: result.to,
                  fromHour: result.fromHour,
                  toHour: result.toHour,
                  days: result.days,
                  status: result.authStatus,
                }}
              />
            ))
          ) : (
            <div
              className="accessTypeEmptyState"
              style={{ padding: "10px 16px", minHeight: "unset" }}
            >
              <Typography variant="h5">
                {inheritedAuthorizations
                  ? "El acompañante no tiene autorizaciones vigentes."
                  : t(BundleKey.ACCESS_TYPE_FORM_NO_AUTORIZACIONES_VIGENTES)}
              </Typography>
            </div>
          )}
        </div>
      </div>

      {/* Selected Employees under Title "Seleccionado en guardia" */}
      {selectedEmployees.length > 0 && (
        <div className="accessTypeSection">
          <p className="accessTypeSectionTitle">
            {t(BundleKey.ACCESS_TYPE_FORM_SELECCIONADO_EN_GUARDIA)}
          </p>
          <div className="accessTypeResultsList">
            {selectedEmployees.map((result: any) => (
              <AccessTypeResultCard
                key={result.id}
                label={result.company}
                status={result.status}
                checked={isChecked(result)}
                managerName={result.employee}
                managerPhone={result.phone}
                phoneLabel={result.phoneLabel}
                variant="guardia"
                checkboxPosition="left"
                onCheckedChange={(checked) => handleCheckedChange(result, checked)}
                disabled={disabled}
                showAuthorizationDetails={!!result.authStatus}
                authorizationSchedule={
                  result.authStatus
                    ? {
                        from: result.from,
                        to: result.to,
                        fromHour: result.fromHour,
                        toHour: result.toHour,
                        days: result.days,
                        status: result.authStatus,
                      }
                    : undefined
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* Registrar ingreso manual search section */}
      {!(isExitForm && disabled) && (
        <div className="accessTypeSection">
          <p className="accessTypeSectionTitle">
            {t(
              isExitForm
                ? BundleKey.ACCESS_TYPE_FORM_REGISTRAR_MANUAL_EXIT
                : BundleKey.ACCESS_TYPE_FORM_REGISTRAR_MANUAL
            )}
          </p>
          <FormTextInputWithButton
            name={searchFieldName}
            placeholder={t(BundleKey.ACCESS_TYPE_FORM_SEARCH_PLACEHOLDER)}
            className="accessTypeSearchInput"
            leftIcon={<SearchIcon />}
            buttonLabel={t(BundleKey.ACCESS_TYPE_FORM_SEARCH_BUTTON)}
            buttonVariant="neutral"
            buttonSize="large"
            buttonClassName="accessTypeSearchButton"
            disabled={disabled}
            showRightButton={false}
          />

          {isEmployeeSearchLoading && (
            <div className="accessTypeSearchLoading">
              <CircularProgress size={30} />
            </div>
          )}

          {!isEmployeeSearchLoading &&
            normalizedSearch.length >= 2 &&
            searchResults.length === 0 && (
              <div className="accessTypeEmptyState" style={{ marginTop: 12 }}>
                <Typography variant="h5">
                  {t(BundleKey.DETAIL_AUTHORIZATION_SEARCH_NOTFOUND)}
                </Typography>
              </div>
            )}

          {searchResults.length > 0 && (
            <div className="accessTypeResultsList">
              {searchResults.map((result: any) => (
                <AccessTypeResultCard
                  key={result.id}
                  label={result.company}
                  status={result.status}
                  checked={isChecked(result)}
                  managerName={result.employee}
                  managerPhone={result.phone}
                  variant="gray"
                  checkboxPosition="left"
                  onCheckedChange={(checked) => handleCheckedChange(result, checked)}
                  disabled={disabled}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (isCompanion) {
    return (
      <div
        className={[className, hasError ? "accessTypeSectionError" : ""].filter(Boolean).join(" ")}
        id={id}
      >
        {content}
      </div>
    );
  }

  return (
    <AccordionCard
      className={[className, hasError ? "accessTypeSectionError" : ""].filter(Boolean).join(" ")}
      id={id}
      titleKey={BundleKey.ACCESS_TYPE_FORM_AUTORIZACIONES_VIGENTES}
      expanded={true}
    >
      {content}
    </AccordionCard>
  );
};

export default AuthorizationsSection;
